import type { FC, ReactNode } from 'react'

import { Compass, HeartHandshake, Target } from 'lucide-react'
import { formatNumber } from '@/lib/format'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { PillLink } from '@/components/generals/pill-link'
import { SectionLabel } from '@/components/generals/section-label'
import Cta from '@/components/landing/cta'

import {
    mission,
    reliabilityFeatures,
    stats,
    values,
    vision
} from '@/constants/home'

const About: FC = (): ReactNode => {
    const years = stats[0]

    return (
        <Layout>
            <Seo
                title='Sobre'
                description="Conheça a Ativa Comércio Exterior: profissionais experientes em comércio internacional, com assessoria documental, logística e tributária aduaneira e uma rede de parceiros em todo o Brasil."
            />

            <section
                data-header-theme='dark'
                className='relative isolate overflow-hidden bg-ink pb-20 pt-44 text-white md:pb-28 lg:pt-60'
            >
                <div aria-hidden='true' className='starfield absolute inset-0 -z-10 opacity-50' />
                <div
                    aria-hidden='true'
                    className='absolute inset-x-0 top-0 -z-10 h-full bg-[radial-gradient(55%_60%_at_20%_0%,rgba(20,130,220,0.4),transparent_75%)]'
                />

                <Container>
                    <SectionLabel tone='dark'>Sobre</SectionLabel>

                    <h1 className='mt-6 max-w-4xl font-heading text-[2.2rem] uppercase leading-[1.02] tracking-[-0.02em] md:text-6xl lg:text-7xl'>
                        Nossa história além das fronteiras
                    </h1>

                    <p className='mt-8 max-w-2xl text-lg leading-relaxed text-white/70'>
                        A Ativa é formada por profissionais experientes das mais diversas áreas do comércio internacional, com assessoria completa nas áreas documental, logística e tributária aduaneira, além de suporte estratégico à tomada de decisões comerciais.
                    </p>
                </Container>
            </section>

            <section id='quem-somos' data-header-theme='light' className='scroll-mt-20 bg-white py-24 md:py-36'>
                <Container>
                    <div className='grid gap-12 lg:grid-cols-12 lg:gap-8'>
                        <div className='lg:col-span-7'>
                            <SectionLabel>Quem somos</SectionLabel>

                            <h2 className='reveal mt-6 font-heading text-[2.1rem] uppercase leading-[1.04] tracking-[-0.02em] md:text-6xl lg:text-[4.1rem]'>
                                <span className='text-steel'>Segurança, agilidade</span>
                                <br />
                                e conformidade.
                            </h2>

                            <div className='mt-12 inline-flex items-end gap-5 border-t pt-8'>
                                <p className='font-heading text-6xl leading-none tracking-[-0.03em] md:text-7xl'>
                                    {formatNumber(years.value, years.decimals)}
                                    {years.suffix}
                                </p>

                                <p className='max-w-[14rem] pb-1 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground'>
                                    {years.label}
                                </p>
                            </div>
                        </div>

                        <div className='flex flex-col justify-end gap-6 lg:col-span-4 lg:col-start-9'>
                            <img
                                src='/images/ship-fog.jpg'
                                alt="Navio porta-contêineres navegando em mar aberto"
                                loading='lazy'
                                decoding='async'
                                className='reveal aspect-[4/3] w-full rounded-2xl object-cover grayscale'
                            />

                            <p className='text-lg leading-relaxed text-foreground'>
                                A Ativa é uma empresa com mais de 30 anos de atuação em comércio exterior e logística internacional, oferecendo assessoria especializada para importadores e exportadores que buscam segurança, agilidade e conformidade em suas operações.
                            </p>

                            <PillLink href='/servicos' variant='outline-dark' className='self-start'>
                                Nossos serviços
                            </PillLink>
                        </div>
                    </div>

                    <div className='mt-20 md:mt-28'>
                        <SectionLabel>Nossa estrutura</SectionLabel>

                        <ul className='mt-6 grid border-l border-t md:grid-cols-3'>
                            {reliabilityFeatures.map((feature, index) => {
                                const Icon = feature.icon

                                return (
                                    <li key={feature.title} className='flex flex-col gap-10 border-b border-r p-6 md:p-8'>
                                        <div className='flex items-center justify-between'>
                                            <span className='flex size-10 items-center justify-center rounded-lg bg-ink text-white'>
                                                <Icon aria-hidden='true' className='size-4' />
                                            </span>

                                            <span className='font-mono text-[11px] text-muted-foreground'>
                                                {String(index + 1).padStart(2, '0')}
                                            </span>
                                        </div>

                                        <div>
                                            <h3 className='font-heading text-lg uppercase leading-snug tracking-[-0.01em]'>
                                                {feature.title}
                                            </h3>

                                            <p className='mt-3 leading-relaxed text-muted-foreground'>
                                                {feature.text}
                                            </p>
                                        </div>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>
                </Container>
            </section>

            <section id='missao-visao-valores' data-header-theme='light' className='scroll-mt-20 bg-secondary py-24 md:py-32'>
                <Container>
                    <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
                        <div className='lg:col-span-7'>
                            <SectionLabel>O que nos move</SectionLabel>

                            <h2 className='reveal mt-6 font-heading text-[2.1rem] uppercase leading-[1.04] tracking-[-0.02em] md:text-5xl lg:text-[3.4rem]'>
                                <span className='text-steel'>Missão, visão</span>
                                <br />
                                e valores.
                            </h2>
                        </div>
                    </div>

                    <div className='mt-12 grid gap-5 lg:grid-cols-3'>
                        <article className='flex flex-col gap-10 rounded-3xl bg-white p-7 md:p-8'>
                            <span className='flex size-10 items-center justify-center rounded-lg bg-brand text-white'>
                                <Target aria-hidden='true' className='size-4' />
                            </span>

                            <div>
                                <h3 className='font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground'>
                                    Missão
                                </h3>

                                <p className='mt-4 text-lg leading-relaxed text-foreground'>
                                    {mission}
                                </p>
                            </div>
                        </article>

                        <article className='flex flex-col gap-10 rounded-3xl bg-white p-7 md:p-8'>
                            <span className='flex size-10 items-center justify-center rounded-lg bg-brand text-white'>
                                <Compass aria-hidden='true' className='size-4' />
                            </span>

                            <div>
                                <h3 className='font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground'>
                                    Visão
                                </h3>

                                <p className='mt-4 text-lg leading-relaxed text-foreground'>
                                    {vision}
                                </p>
                            </div>
                        </article>

                        <article className='flex flex-col gap-10 rounded-3xl bg-ink p-7 text-white md:p-8'>
                            <span className='flex size-10 items-center justify-center rounded-lg bg-highlight text-ink'>
                                <HeartHandshake aria-hidden='true' className='size-4' />
                            </span>

                            <div>
                                <h3 className='font-mono text-[11px] uppercase tracking-[0.16em] text-white/60'>
                                    Valores
                                </h3>

                                <ul className='mt-4 space-y-3'>
                                    {values.map((value) => (
                                        <li key={value} className='flex items-start gap-3 text-lg leading-snug'>
                                            <span aria-hidden='true' className='mt-2.5 size-1.5 shrink-0 bg-highlight' />
                                            {value}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </article>
                    </div>
                </Container>
            </section>

            <Cta />
        </Layout>
    )
}

export default About
